import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../App';
import { initEcho } from '../../services/notificationService';
import api from '../../services/api';
import { 
  MessageSquare, X, Send, User, Circle, 
  Volume2, VolumeX, ChevronLeft, Minus, 
  Maximize2, SendHorizonal, Smile
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function ChatWidget() {
    const { user, isStaff } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [typingUsers, setTypingUsers] = useState(new Set());
    const [onlineUsers, setOnlineUsers] = useState(new Set());
    const [isMuted, setIsMuted] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const chatEndRef = useRef(null);
    const audioRef = useRef(null);
    const selectedUserRef = useRef(null);

    // Sincronizar el ref con el estado para que los listeners de socket lo vean
    useEffect(() => {
        selectedUserRef.current = selectedUser;
    }, [selectedUser]);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadUsers = useCallback(async () => {
        try {
            const usersRes = await api.get('/chat/users');
            setUsers(usersRes.data);
        } catch (err) {
            console.error('Error al cargar usuarios de chat');
        }
    }, []);

    const loadMessages = async (targetUserId, beforeId = null) => {
        setLoadingMessages(true);
        try {
            const res = await api.get(`/chat/messages/${targetUserId}`, {
                params: { before_id: beforeId, limit: 50 }
            });
            
            if (beforeId) {
                setMessages(prev => [...res.data, ...prev]);
            } else {
                setMessages(res.data);
            }
            setHasMore(res.data.length === 50);
        } catch (err) {
            toast.error('Error al cargar historial de chat');
        } finally {
            setLoadingMessages(false);
            if (!beforeId) setTimeout(scrollToBottom, 100);
        }
    };

    useEffect(() => {
        const canChat = user && ['super_admin', 'admin', 'boss', 'staff'].includes(user?.role);
        if (!canChat) return;

        const token = JSON.parse(localStorage.getItem('cbr_auth') || '{}').token;
        if (!token) return;

        const echo = initEcho(token);
        if (!echo) return;

        loadUsers();

        echo.join('presence-chat')
            .here((users) => {
                setOnlineUsers(new Set(users.map(u => Number(u.id))));
            })
            .joining((u) => {
                setOnlineUsers(prev => new Set(prev).add(Number(u.id)));
            })
            .leaving((u) => {
                setOnlineUsers(prev => {
                    const next = new Set(prev);
                    next.delete(Number(u.id));
                    return next;
                });
            })
            .listenForWhisper('typing', (e) => {
                if (e.targetId === user.id) {
                    setTypingUsers(prev => new Set(prev).add(e.senderId));
                }
            })
            .listenForWhisper('stop_typing', (e) => {
                if (e.targetId === user.id) {
                    setTypingUsers(prev => {
                        const next = new Set(prev);
                        next.delete(e.senderId);
                        return next;
                    });
                }
            });

        echo.private(`chat.${user.id}`)
            .listen('MessageSent', (e) => {
                const msg = e.message;
                const currentSelected = selectedUserRef.current;
                
                if (currentSelected && Number(currentSelected.id) === Number(msg.sender_id)) {
                    setMessages(prev => [...prev, msg]);
                    api.patch(`/chat/read/${msg.sender_id}`).catch(() => {});
                    if (!isMuted) audioRef.current?.play().catch(() => {});
                } else {
                    setUsers(prev => {
                        let found = false;
                        const next = prev.map(u => {
                            if (Number(u.id) === Number(msg.sender_id)) {
                                found = true;
                                return { ...u, unread_count: Number(u.unread_count || 0) + 1 };
                            }
                            return u;
                        });
                        
                        if (!found) {
                            loadUsers();
                            return prev;
                        }
                        
                        return next;
                    });
                    
                    if (!isMuted) audioRef.current?.play().catch(() => {});
                    toast.success('Mensaje interno', { 
                        icon: '💬',
                        style: { background: 'var(--primary)', color: 'white' }
                    });
                }
            });

        return () => {
            console.log('Desconectando Echo de chat...');
            echo.leave('presence-chat');
            echo.leave(`chat.${user.id}`);
        };
    }, [user?.id, user?.role, loadUsers, isMuted]); // Dependencias estables


    const handleSelectUser = (u) => {
        setSelectedUser(u);
        setIsMinimized(false);
        loadMessages(u.id);
        
        if (Number(u.unread_count) > 0) {
            api.patch(`/chat/read/${u.id}`);
            setUsers(prev => prev.map(usr => Number(usr.id) === Number(u.id) ? { ...usr, unread_count: 0 } : usr));
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedUser) return;

        try {
            const res = await api.post('/chat/messages', {
                receiver_id: selectedUser.id,
                message: newMessage
            });

            setMessages(prev => [...prev, res.data]);
            setNewMessage('');
            setTimeout(scrollToBottom, 50);
        } catch (err) {
            toast.error('Error al enviar mensaje');
        }
    };

    if (!isStaff?.()) return null;

    const totalUnread = (users || []).reduce((acc, u) => acc + Number(u?.unread_count || 0), 0);

    return (
        <div style={{ zIndex: 9999, position: 'relative' }}>
            <audio ref={audioRef} src="https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3" preload="auto" />
            
            {/* ── BOTÓN FLOTANTE (FAB) ────────────────────────────────────────── */}
            <style>{`
                @keyframes pulse-chat {
                    0% { box-shadow: 0 0 0 0 rgba(0, 51, 102, 0.4); }
                    70% { box-shadow: 0 0 0 15px rgba(0, 51, 102, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(0, 51, 102, 0); }
                }
                .fab-chat {
                    position: fixed;
                    bottom: 2rem;
                    right: 2rem;
                    width: 64px;
                    height: 64px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    box-shadow: 0 8px 32px rgba(0, 51, 102, 0.3);
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    border: 2px solid rgba(255,255,255,0.2);
                    z-index: 1000;
                }
                .fab-chat:hover {
                    transform: scale(1.1) rotate(5deg);
                    box-shadow: 0 12px 40px rgba(0, 51, 102, 0.4);
                }
                .fab-chat.has-unread {
                    animation: pulse-chat 2s infinite;
                }
                .unread-badge {
                    position: absolute;
                    top: -5px;
                    right: -5px;
                    background: var(--error);
                    color: white;
                    font-size: 0.7rem;
                    font-weight: 800;
                    padding: 4px 8px;
                    border-radius: 20px;
                    border: 2px solid white;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
                }

                .chat-window {
                    position: fixed;
                    bottom: 7rem;
                    right: 2rem;
                    width: 400px;
                    max-width: calc(100vw - 4rem);
                    background: rgba(255, 255, 255, 0.7);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.5);
                    border-radius: 24px;
                    box-shadow: 0 20px 80px rgba(0,0,0,0.15);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                    transform-origin: bottom right;
                }
                .chat-window.minimized {
                    height: 0;
                    opacity: 0;
                    transform: scale(0.8) translateY(20px);
                    pointer-events: none;
                }
                .chat-window.open {
                    height: 600px;
                    max-height: calc(100vh - 10rem);
                    opacity: 1;
                    transform: scale(1) translateY(0);
                }

                .chat-header {
                    padding: 1.5rem;
                    background: linear-gradient(to right, var(--primary), var(--primary-dark));
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .chat-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 1rem;
                    background: rgba(248, 250, 252, 0.4);
                }
                
                .chat-body::-webkit-scrollbar { width: 6px; }
                .chat-body::-webkit-scrollbar-track { background: transparent; }
                .chat-body::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; }

                .msg-bubble {
                    padding: 12px 16px;
                    border-radius: 18px;
                    font-size: 0.9rem;
                    line-height: 1.4;
                    max-width: 85%;
                    position: relative;
                    margin-bottom: 4px;
                    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
                }
                .msg-mine {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
                    color: white;
                    align-self: flex-end;
                    border-bottom-right-radius: 4px;
                }
                .msg-theirs {
                    background: white;
                    color: var(--text-main);
                    align-self: flex-start;
                    border-bottom-left-radius: 4px;
                    border: 1px solid rgba(0,0,0,0.05);
                }

                .user-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    border-radius: 16px;
                    transition: all 0.2s;
                    cursor: pointer;
                    margin-bottom: 8px;
                    background: rgba(255,255,255,0.3);
                    border: 1px solid transparent;
                }
                .user-item:hover {
                    background: white;
                    transform: translateX(4px);
                    border-color: var(--accent-light);
                    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
                }
            `}</style>

            {!isOpen ? (
                <div 
                    className={`fab-chat ${totalUnread > 0 ? 'has-unread' : ''}`}
                    onClick={() => setIsOpen(true)}
                >
                    <MessageSquare size={28} />
                    {totalUnread > 0 && <span className="unread-badge">{totalUnread}</span>}
                </div>
            ) : (
                <div className={`chat-window ${isOpen ? 'open' : 'minimized'}`}>
                    
                    <div className="chat-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {selectedUser ? (
                                <button 
                                    onClick={() => setSelectedUser(null)}
                                    style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                            ) : (
                                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '8px', borderRadius: '12px' }}>
                                    <MessageSquare size={20} />
                                </div>
                            )}
                            <div>
                                <h4 style={{ color: 'white', margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                                    {selectedUser ? selectedUser.name : 'Comunicación Interna'}
                                </h4>
                                <span style={{ fontSize: '0.7rem', opacity: 0.8, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {selectedUser 
                                        ? (typingUsers.has(Number(selectedUser.id)) ? 'Escribiendo...' : (onlineUsers.has(Number(selectedUser.id)) ? '• En línea' : 'Desconectado'))
                                        : `${users.length} Colaboradores disponibles`
                                    }
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button 
                                onClick={(e) => {
                                    e.preventDefault();
                                    const nextMuted = !isMuted;
                                    setIsMuted(nextMuted);
                                    if (!nextMuted) {
                                        audioRef.current?.play().catch(() => {});
                                    }
                                }} 
                                style={{ background: 'none', border: 'none', color: 'white', opacity: 0.7, cursor: 'pointer' }}
                            >
                                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                            </button>
                            <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: 'white', opacity: 0.7, cursor: 'pointer' }}>
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    <div className="chat-body" style={{ display: 'flex', flexDirection: 'column' }}>
                        {!selectedUser ? (
                            <div style={{ padding: '0.5rem' }}>
                                {users.map(u => (
                                    <div key={u.id} className="user-item" onClick={() => handleSelectUser(u)}>
                                        <div style={{ position: 'relative' }}>
                                            <div style={{ width: 44, height: 44, borderRadius: '14px', background: 'var(--primary-light)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem' }}>
                                                {u.name[0]}
                                            </div>
                                            <div style={{ position: 'absolute', bottom: -2, right: -2, width: 14, height: 14, borderRadius: '50%', border: '2px solid white', background: onlineUsers.has(u.id) ? 'var(--success)' : '#cbd5e1' }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{u.name}</span>
                                                {u.unread_count > 0 && <span style={{ background: 'var(--primary)', color: 'white', fontSize: '0.65rem', padding: '2px 8px', borderRadius: '10px' }}>{u.unread_count}</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {hasMore && (
                                    <button 
                                        onClick={() => loadMessages(selectedUser.id, messages[0]?.id)}
                                        style={{ background: 'transparent', border: 'none', color: 'var(--primary)', fontSize: '0.75rem', padding: '10px', cursor: 'pointer' }}
                                    >
                                        {loadingMessages ? 'Cargando mensajes...' : 'Cargar historial previo'}
                                    </button>
                                )}
                                {messages.map((m, idx) => {
                                    const isMine = m.sender_id === user.id;
                                    return (
                                        <div key={m.id || idx} className={`msg-bubble ${isMine ? 'msg-mine' : 'msg-theirs'}`}>
                                            {m.message}
                                            <div style={{ fontSize: '0.6rem', marginTop: 4, opacity: 0.7, textAlign: isMine ? 'right' : 'left' }}>
                                                {new Date(m.created_at).toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={chatEndRef} />
                            </div>
                        )}
                    </div>

                    {selectedUser && (
                        <form 
                            onSubmit={handleSendMessage}
                            style={{ padding: '1.2rem', background: 'white', borderTop: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: '10px', alignItems: 'center' }}
                        >
                            <div style={{ flex: 1, position: 'relative' }}>
                                <input 
                                    type="text"
                                    placeholder="Escribe algo increíble..."
                                    style={{ borderRadius: '12px', background: 'var(--bg-body)', padding: '12px 16px', fontSize: '0.85rem', border: '1px solid transparent' }}
                                    value={newMessage}
                                    onChange={(e) => {
                                        setNewMessage(e.target.value);
                                        const token = JSON.parse(localStorage.getItem('cbr_auth') || '{}').token;
                                        if (token) {
                                            const echo = initEcho(token);
                                            if (echo) {
                                                echo.join('presence-chat').whisper('typing', { senderId: user.id, targetId: selectedUser.id });
                                                clearTimeout(window.typingTimeout);
                                                window.typingTimeout = setTimeout(() => {
                                                    echo.join('presence-chat').whisper('stop_typing', { senderId: user.id, targetId: selectedUser.id });
                                                }, 2000);
                                            }
                                        }
                                    }}
                                />
                                <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.3 }}>
                                    <Smile size={18} />
                                </div>
                            </div>
                            <button 
                                type="submit"
                                style={{ 
                                    background: 'var(--primary)', 
                                    color: 'white', 
                                    width: 44, 
                                    height: 44, 
                                    borderRadius: '12px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    border: 'none', 
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <SendHorizonal size={20} />
                            </button>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
}
